<template>
    <div :data-testid="`${props.dataTestId}-content`">
        <div ref="contentElement" :class="collapsed ? 'see-more--collapsed' : 'see-more--expanded'">
            <slot class="see-more__content" />
        </div>
        <ClientOnly>
            <template v-if="isVisible">
                <VJoyLink v-if="!isLocked" class="see-more__button" :text="label" rel="nofollow" @click="handleClick" />
                <VJoyLink
                    v-else
                    class="see-more__button"
                    :text="locked?.label"
                    :href="lockedHref"
                    icon="lock"
                    rel="nofollow"
                    @click="handleClickOnLock"
                ></VJoyLink>
            </template>
            <template #fallback>
                <VJoyFormFieldSkeleton class="see-more__button" size="xxsmall" width="80px" />
            </template>
        </ClientOnly>
    </div>
</template>
<script setup lang="ts">
    import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useRoute, useTranslation} from '#imports';
    import {VJoyFormFieldSkeleton, VJoyLink} from '@maltjoy/core-vue';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';

    export type SeeMoreProps = {
        maxLines?: number;
        dataTestId?: string;
        content?: string;
        locked?:
            | {allowHref: false; label: string} // No href, event sent
            | {allowHref: true; label: string; componentName: string; targetId: string}; // default href
        defaultState?: 'collapsed' | 'expanded';
    };

    const props = withDefaults(defineProps<SeeMoreProps>(), {
        maxLines: 4,
        dataTestId: 'see-more-component',
        defaultState: 'collapsed',
    });

    const {t} = useTranslation();
    const route = useRoute();
    const emits = defineEmits(['seeMoreClickedWhenLocked']);

    // Expand/Collapse
    const maxLinesCssVariable = `${props.maxLines}`;
    const collapsed = ref<boolean>(props.defaultState === 'collapsed');
    const label = computed(() => (collapsed.value ? t('profile.section.see.more') : t('profile.section.see.less')));
    function handleClick(e: MouseEvent) {
        e.preventDefault();
        collapsed.value = !collapsed.value;
    }

    // Visibility
    const contentElement = ref<HTMLElement>();
    const isVisible = ref<boolean>(true);

    watch(
        () => props.content,
        async () => {
            await nextTick();
            computeVisibility();
        },
    );

    onMounted(() => {
        computeVisibility();
        window.addEventListener('resize', computeVisibility);
    });
    onUnmounted(() => window.removeEventListener('resize', computeVisibility));

    function computeVisibility() {
        if (!contentElement.value || !collapsed.value) return;
        isVisible.value = contentElement.value.scrollHeight > contentElement.value.clientHeight;
    }

    // Lock
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const isLocked = computed(() => !!props.locked && sectionVisibility.value[SectionsVisibilityType.VIEW_MORE_LOCK]);
    const lockedHref = computed(() => {
        if (!props.locked?.allowHref) {
            return undefined;
        }
        const redirectParam = encodeURIComponent(`/profile${route.path}#${props.locked.targetId}`);
        return `/who-are-you?redirect=${redirectParam}&pageSource=profile_page&componentSource=${props.locked.componentName}`;
    });
    function handleClickOnLock() {
        emits('seeMoreClickedWhenLocked');
    }
</script>
<style scoped lang="scss">
    .see-more {
        &__button {
            margin-top: var(--joy-core-spacing-2);
            font-weight: normal;
        }

        &--collapsed {
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: pre-wrap;
            -webkit-line-clamp: v-bind(maxLinesCssVariable);
            -webkit-box-orient: vertical;
        }

        &--expanded {
            white-space: pre-wrap;
            max-height: none;
            overflow: visible;
        }
    }
</style>
